import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import {
	ImportJobStatus,
	ImportTarget,
	Services,
} from '@ekhodealer/ekho-common';
import { Dropdown, SkeletonRect } from '@ekhodealer/ekho-common/rev';

import type {
	InventoryServiceConfig} from '@ekhodealer/ekho-common';
import type { ActionMenuItemProps} from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';
import { formatTimestampToDate, formatTimestampToTime } from '@common/lib/time';

import { useGetImportJobs } from '../../../api/useGetImportJobs';
import { importJobIdFilterAtom } from '../../../atoms/inventoryAtoms';
import { useSetFilterAtom } from '../../../hooks/useSetFilterAtom';
import { FilterSection } from '../FilterSection';

const LoadingDropdown = () => {
	return <SkeletonRect width='100%' height='32px' />;
};

/**
 * ImportJobSection component that provides import job filtering options
 * Shows recent import jobs as a dropdown for single selection
 * Only renders if import is enabled for the services provider
 */
export function ImportJobSection() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const isImportEnabled =
		(servicesProvider?.config?.[Services.INVENTORY] as InventoryServiceConfig | undefined)
			?.adminPortalConfig?.importInventoryEnabled ?? false;

	const currentImportJobId = useAtomValue(importJobIdFilterAtom);
	const setImportJobId = useSetFilterAtom(importJobIdFilterAtom);
	const { data: importJobs, isPending } = useGetImportJobs({
		target: ImportTarget.INVENTORY,
		status: ImportJobStatus.COMPLETED,
		limit: 10,
	});

	// Build import job options for dropdown
	const importJobOptions = useMemo<ActionMenuItemProps[]>(() => {
		if (!importJobs) return [];

		const options: ActionMenuItemProps[] = [
			{
				id: 'all',
				label: 'All imports',
				onAction: () => setImportJobId(null),
			},
		];

		importJobs.forEach((job) => {
			const timestamp = job.completedAt || job.startedAt;
			const displayDate = timestamp ? formatTimestampToDate(timestamp) : undefined;
			const displayTime = timestamp ? formatTimestampToTime(timestamp) : undefined;

			const displayLabel =
				displayDate && displayTime
					? `${displayDate} ${displayTime}`
					: displayDate || 'Unknown date';

			options.push({
				id: job.id,
				label: displayLabel,
				onAction: () => setImportJobId(job.id),
			});
		});

		return options;
	}, [importJobs, setImportJobId]);

	// Current import job value for dropdown (use 'all' if null)
	const currentValue = currentImportJobId || 'all';

	const handleSelectionChange = (value: string) => {
		if (value === 'all') {
			setImportJobId(null);
		} else {
			setImportJobId(value);
		}
	};

	// Don't render if import is not enabled
	if (!isImportEnabled) {
		return null;
	}

	return (
		<FilterSection title='Recent imports'>
			{isPending ? (
				<LoadingDropdown />
			) : importJobOptions.length > 1 ? (
				<Dropdown
					options={importJobOptions}
					value={currentValue}
					onSelectionChange={handleSelectionChange}
					buttonVariant='outline'
					size='large'
				/>
			) : null}
		</FilterSection>
	);
}
